import { Map as MLMap } from 'maplibre-gl';
import { type AnimatedPacket, PULSE_STROKE_PX, FLIGHT_MS, SETTLE_HOLD_MS, FADE_MS, CLICKABLE_MS, animationLifetimeMs, PULSE_MS, PULSE_MIN_SIZE_PX, PULSE_MAX_SIZE_PX, PULSE_PEAK_OPACITY } from '../../lib/animation';
import { type ProtocolFilter } from '../../state/AppData';
import { type ProvenanceScope, packetInProvenanceScope } from '../../lib/definitions/trust';
import { useRef, useEffect } from 'react';
import { IN_FLIGHT_COLOR, packetCategoryInfo, OUT_OF_SCOPE_DIM } from '../../lib/definitions/protocols';
import { relativeTime } from '../../lib/format';
import { colorFor } from '../../lib/definitions/palette';
import { regionInScope } from '../../lib/regions';
import type { RegionScopeArgs } from '../../lib/liveMapData';
import { burstAt, orbAt, prefersReducedMotion } from '../../lib/pathFx';


// ---------------------------------------------------------------------
// Imperative per-frame animation overlay. Kept out of React's render loop
// on purpose (BUILD THIS #5's performance note / spec §4.5's rendering
// split): the static node layer is GL, the small set of currently-animating
// rays/markers is a thin DOM overlay so each one is a real, individually
// clickable/hit-testable object without paying React re-render cost every
// frame.
export function useAnimationOverlay(
  mapRef: React.MutableRefObject<MLMap | null>,
  overlayRef: React.MutableRefObject<HTMLDivElement | null>,
  animations: AnimatedPacket[],
  select: (s: { kind: "packet"; packet: AnimatedPacket["packet"] }) => void,
  protocolFilter: ProtocolFilter,
  provenanceScope: ProvenanceScope,
  regionScope: RegionScopeArgs,
  selectedNodeId: string | null,
  onHover: (h: { x: number; y: number; label: string } | null) => void,
) {
  const elsRef = useRef<Map<string, { root: HTMLDivElement; anim: AnimatedPacket }>>(new Map());
  const selectRef = useRef(select);
  selectRef.current = select;
  const animationsRef = useRef(animations);
  animationsRef.current = animations;
  const filterRef = useRef(protocolFilter);
  filterRef.current = protocolFilter;
  const provenanceScopeRef = useRef(provenanceScope);
  provenanceScopeRef.current = provenanceScope;
  const regionScopeRef = useRef(regionScope);
  regionScopeRef.current = regionScope;
  const selectedNodeIdRef = useRef(selectedNodeId);
  selectedNodeIdRef.current = selectedNodeId;
  const onHoverRef = useRef(onHover);
  onHoverRef.current = onHover;

  // Sync DOM nodes to the animations array.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const live = new Set(animations.map((a) => a.key));

    for (const [key, entry] of elsRef.current) {
      if (!live.has(key)) {
        entry.root.remove();
        elsRef.current.delete(key);
      }
    }

    for (const anim of animations) {
      if (elsRef.current.has(anim.key)) continue;
      const root = document.createElement("div");
      root.style.position = "absolute";
      root.style.inset = "0";
      root.style.pointerEvents = "none";

      // Pulse ring sizing/timing: see lib/animation.ts's own header comment
      // for the full "shorter and brighter beats larger and longer"
      // reasoning — this addresses a failure where no "live"
      // packet was visible: the prior tuning started at a flat 0.4 opacity and only
      // got dimmer over 4.2s, which rendered correctly and could not be
      // seen against ~1,600 positioned dots). PULSE_STROKE_PX (2px, not
      // the earlier 1px) keeps the ring legible as a ring rather than a
      // soft dot at these sizes on a hi-DPI basemap.
      const ringFrom = document.createElement("div");
      ringFrom.className = "anim-ring";
      Object.assign(ringFrom.style, {
        position: "absolute",
        borderRadius: "50%",
        border: `${PULSE_STROKE_PX}px solid ${colorForAnimCategory(anim)}`,
      } as CSSStyleDeclaration);
      root.appendChild(ringFrom);

      let ringTo: HTMLDivElement | null = null;
      if (anim.to) {
        // The arrival at the receiving station: a half-opaque orb that swells and fades (lib/pathFx.ts's orbAt), with
        // the solid burst drawn by the marker below, the same landing the map's walks use.
        ringTo = document.createElement("div");
        Object.assign(ringTo.style, {
          position: "absolute",
          borderRadius: "50%",
          background: colorForAnimCategory(anim),
        } as CSSStyleDeclaration);
        root.appendChild(ringTo);
      }

      const ray = document.createElement("div");
      Object.assign(ray.style, {
        position: "absolute",
        height: "1px",
        background: IN_FLIGHT_COLOR,
        transformOrigin: "0 50%",
        pointerEvents: "none",
      } as CSSStyleDeclaration);
      root.appendChild(ray);

      const marker = document.createElement("div");
      marker.className = "anim-marker";
      Object.assign(marker.style, {
        width: "5px",
        height: "5px",
        background: IN_FLIGHT_COLOR,
        boxShadow: `0 0 3px 1px ${IN_FLIGHT_COLOR}`,
      } as CSSStyleDeclaration);
      root.appendChild(marker);

      const hit = document.createElement("div");
      hit.className = "anim-hit";
      hit.style.pointerEvents = "auto";
      hit.onclick = () => selectRef.current({ kind: "packet", packet: anim.packet });
      // Hover preview (spec §5: "hover on a settled/resting ray or ring
      // ... shows a one-line summary (type + age) before committing to a
      // click"). Reuses the exact same styled .node-tooltip the node-hover
      // path already renders (via the shared onHover/setHover callback)
      // rather than a plain browser title attribute, which is what this
      // used to be — no age, unstyled, and not dismissable on demand.
      // Scoped to the existing 28px hit circle at the ray's resting
      // endpoint rather than hit-testing the full ray line itself: that's
      // already where a user naturally hovers to preview before clicking,
      // and building rotated-line hit-testing for the same practical
      // coverage isn't worth the added complexity.
      const hoverLabel = () => `${packetCategoryInfo(anim.packet.protocol, anim.packet.type).label} · ${relativeTime(anim.packet.ts)}`;
      hit.addEventListener("mouseenter", (ev) => {
        const rect = overlayRef.current?.getBoundingClientRect();
        onHoverRef.current({ x: ev.clientX - (rect?.left ?? 0), y: ev.clientY - (rect?.top ?? 0), label: hoverLabel() });
      });
      hit.addEventListener("mousemove", (ev) => {
        const rect = overlayRef.current?.getBoundingClientRect();
        onHoverRef.current({ x: ev.clientX - (rect?.left ?? 0), y: ev.clientY - (rect?.top ?? 0), label: hoverLabel() });
      });
      hit.addEventListener("mouseleave", () => onHoverRef.current(null));
      root.appendChild(hit);

      // No transient name label — removed as part of the same declutter
      // (a stack of labels popping up during any real burst competed with
      // everything else on screen). The node's own permanent label at
      // close zoom, plus click-to-open-detail, already cover "what's that."

      overlay.appendChild(root);
      elsRef.current.set(anim.key, { root, anim });
    }
  }, [animations, overlayRef]);

  // The per-frame loop.
  useEffect(() => {
    let raf = 0;
    function tick() {
      const map = mapRef.current;
      const overlay = overlayRef.current;
      if (!map || !overlay) {
        raf = requestAnimationFrame(tick);
        return;
      }
      const now = performance.now();
      for (const { root, anim } of elsRef.current.values()) {
        renderFrame(map, root, anim, now, filterRef.current, provenanceScopeRef.current, regionScopeRef.current, selectedNodeIdRef.current);
      }
      raf = requestAnimationFrame(tick);
    }
    if (animationsRef.current.length > 0 || elsRef.current.size > 0) {
      raf = requestAnimationFrame(tick);
    }
    // Always keep a light loop running so newly-added animations pick up
    // immediately without a dependency-array dance.
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mapRef, overlayRef]);
}


function colorForAnimCategory(anim: AnimatedPacket): string {
  const info = packetCategoryInfo(anim.packet.protocol, anim.packet.type);
  // theme-independent-enough approximation for a DOM-drawn ring; light value
  // reads fine on both raster basemaps thanks to the halo/shadow below.
  return colorFor(info.color, "dark");
}


function renderFrame(
  map: MLMap,
  root: HTMLDivElement,
  anim: AnimatedPacket,
  now: number,
  filter: ProtocolFilter,
  provenanceScope: ProvenanceScope,
  region: RegionScopeArgs,
  selectedNodeId: string | null,
) {
  const elapsed = now - anim.createdAt;
  const fromLngLat = anim.from?.lat != null && anim.from?.lon != null ? [anim.from.lon, anim.from.lat] : null;
  const toLngLat = anim.to?.lat != null && anim.to?.lon != null ? [anim.to.lon, anim.to.lat] : null;

  const children = root.children as unknown as HTMLDivElement[];
  // Element order is fixed at creation time: ring(s), ray, marker, hit.
  const ray = root.querySelector<HTMLDivElement>(".anim-marker") ? children[anim.to ? 2 : 1] : null;
  const marker = root.querySelector<HTMLDivElement>(".anim-marker");
  const hit = root.querySelector<HTMLDivElement>(".anim-hit");
  const rings = anim.to ? [children[0], children[1]] : [children[0]];

  // Protocol is SEPARATION, not emphasis (found that
  // the single-protocol toggle still rendered a dimmed layer of the other
  // protocol's nodes dense enough to obscure the ones actually selected —
  // the two protocols need to be separated unless viewing the ALL
  // page): a protocol-filtered ping is excluded from the map entirely,
  // the same treatment as "no position," never just dimmed. Region stays
  // the one dim-not-hide control (§10's own "preserves geographic
  // context" reasoning is specific to region and was never meant to cover
  // protocol) — same for node-selection dimming (spec §5). Provenance
  // scope ("this radio only" vs "everything we can see") gets the SAME
  // exclude treatment as protocol, not region's dim — see
  // lib/definitions.ts's own header comment: once the firehose vastly
  // outnumbers first-party traffic, a dimmed firehose layer would still
  // visually dominate by sheer volume, defeating the point of the filter.
  if (!fromLngLat || (filter !== "all" && anim.packet.protocol !== filter) || !packetInProvenanceScope(anim.packet.trust_tier, provenanceScope)) {
    root.style.display = "none";
    return;
  }
  root.style.display = "block";

  // Out-of-scope-by-region/selection packets DIM on the map rather than
  // disappear, to keep spatial context (a region view specifically;
  // see above for why protocol no longer shares this treatment), even
  // though they're excluded entirely from the packet feed (see
  // PacketFeed.tsx). Region membership is by the packet's origin node,
  // same convention as PacketFeed/TimelinePanel — "hide outside" isn't
  // offered here, only dim, since a ray disappearing mid-flight would be a
  // more jarring loss of spatial context than a dimmed node.
  const regionScopeOk = regionInScope(anim.from, { selectedCount: region.selectedCount, regionsForNode: region.regionsForNode });
  const nodeSelectionOk =
    selectedNodeId == null ||
    (anim.from != null && String(anim.from.id) === selectedNodeId) ||
    (anim.to != null && String(anim.to.id) === selectedNodeId);
  const dim = regionScopeOk && nodeSelectionOk ? 1 : OUT_OF_SCOPE_DIM;

  const fromPx = map.project(fromLngLat as [number, number]);
  const toPx = toLngLat ? map.project(toLngLat as [number, number]) : null;

  const flightT = Math.min(1, elapsed / FLIGHT_MS);
  const markerPx = toPx
    ? { x: fromPx.x + (toPx.x - fromPx.x) * flightT, y: fromPx.y + (toPx.y - fromPx.y) * flightT }
    : fromPx;

  // Ray: grows from `from` to the moving point during flight, then holds at
  // full length, then fades (spec §11's open question — we choose
  // progressive growth, a documented decision).
  const rayEndPx = flightT < 1 ? markerPx : toPx ?? fromPx;
  const dx = rayEndPx.x - fromPx.x;
  const dy = rayEndPx.y - fromPx.y;
  const len = Math.hypot(dx, dy);
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);

  let rayOpacity = 1;
  const settleEnd = FLIGHT_MS + SETTLE_HOLD_MS;
  if (elapsed > settleEnd) {
    rayOpacity = Math.max(0, 1 - (elapsed - settleEnd) / FADE_MS);
  }
  if (!toPx) rayOpacity = 0; // no second endpoint: pulse-only, no ray to draw
  rayOpacity *= dim;

  if (ray) {
    ray.style.left = `${fromPx.x}px`;
    ray.style.top = `${fromPx.y}px`;
    ray.style.width = `${len}px`;
    ray.style.transform = `rotate(${angle}deg)`;
    ray.style.opacity = String(rayOpacity);
  }

  const reduced = prefersReducedMotion();
  const flying = elapsed < CLICKABLE_MS;
  if (marker) {
    if (toPx && flightT >= 1) {
      // Landed at the receiver: the small solid burst, then gone with the ray's fade.
      const burst = burstAt(elapsed - FLIGHT_MS, reduced);
      if (burst) {
        const d = burst.radiusPx * 2;
        marker.style.display = "block";
        marker.style.width = `${d}px`;
        marker.style.height = `${d}px`;
        marker.style.left = `${toPx.x - d / 2}px`;
        marker.style.top = `${toPx.y - d / 2}px`;
        marker.style.boxShadow = "none";
        marker.style.borderRadius = "50%";
        marker.style.opacity = String(burst.opacity * dim);
      } else {
        marker.style.display = "none";
      }
    } else {
      marker.style.display = flying ? "block" : "none";
      marker.style.width = "5px";
      marker.style.height = "5px";
      marker.style.boxShadow = `0 0 3px 1px ${IN_FLIGHT_COLOR}`;
      marker.style.left = `${markerPx.x - 2.5}px`;
      marker.style.top = `${markerPx.y - 2.5}px`;
      marker.style.borderRadius = "50%";
      marker.style.opacity = String(dim);
    }
  }
  if (hit) {
    // The hit target stays fully clickable even when dimmed — "dim, not
    // hide" means a dimmed packet's detail is still one click away. Its
    // own lifetime (animationLifetimeMs) is decoupled from the pulse
    // ring's much shorter PULSE_MS: a resolved ray/marker keeps animating
    // for the full CLICKABLE_MS and must stay clickable for exactly that
    // long, regardless of how quickly the origin pulse itself has faded.
    const hitPx = flightT < 1 ? markerPx : toPx ?? fromPx;
    hit.style.left = `${hitPx.x}px`;
    hit.style.top = `${hitPx.y}px`;
    hit.style.display = elapsed < animationLifetimeMs(anim) ? "block" : "none";
  }

  // The origin pulse — see lib/animation.ts's own header comment for the
  // full "shorter and brighter beats larger and longer" reasoning. Every
  // packet gets this, broadcast or not; it's deliberately independent of
  // whether a ray was drawn at all.
  const pulseT = Math.min(1, elapsed / PULSE_MS);
  const positions = anim.to ? [fromPx, toPx ?? fromPx] : [fromPx];
  rings.forEach((ringEl, i) => {
    if (!ringEl) return;
    const p = positions[i] ?? fromPx;
    if (i === 1 && anim.to) {
      // The receiving station's orb: starts when the packet lands.
      const orb = orbAt(elapsed - FLIGHT_MS, reduced);
      ringEl.style.display = orb ? "block" : "none";
      if (orb) {
        const d = orb.radiusPx * 2;
        ringEl.style.left = `${p.x - d / 2}px`;
        ringEl.style.top = `${p.y - d / 2}px`;
        ringEl.style.width = `${d}px`;
        ringEl.style.height = `${d}px`;
        ringEl.style.opacity = String(orb.opacity * dim);
      }
      return;
    }
    // The origin pulse. Reduced motion holds it at one size and only fades it.
    const size = reduced ? (PULSE_MIN_SIZE_PX + PULSE_MAX_SIZE_PX) / 2 : PULSE_MIN_SIZE_PX + pulseT * (PULSE_MAX_SIZE_PX - PULSE_MIN_SIZE_PX);
    ringEl.style.left = `${p.x - size / 2}px`;
    ringEl.style.top = `${p.y - size / 2}px`;
    ringEl.style.width = `${size}px`;
    ringEl.style.height = `${size}px`;
    ringEl.style.opacity = String(Math.max(0, (1 - pulseT) * PULSE_PEAK_OPACITY) * dim);
  });
}
